import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import type { MultiWidgetStateStore, TranslateFn, UserMenuOption } from '@typings/plugin-runtime.ts';
import { Button } from '@ui/buttons/Button.tsx';
import { Icon } from '@ui/icons/Icon.tsx';
import Submenu from '@ui/submenu/submenu.tsx';
import { focusRelativeToElement } from '@utils/focus.ts';
import { getMapElement } from '@utils/mapElement.ts';

import { useCSSColors } from '../../../src/hooks/useCSSColors.ts';
import MenuSelection from './MenuSelection.tsx';

// Keep in sync with the selector in src/utils/focus.ts.
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const Wrapper = styled.div`
  &.-mobile {
    /* Positioned relative to .atrius-map (portaled in below), not the viewport -
       avoids position:fixed breaking under host-page CSS containment (AB#1639344). */
    position: absolute;
    top: 60px;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 950;
    display: flex;
    flex-direction: column;
    filter: drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.2));
    overflow-y: auto;
    overflow-x: hidden;
    padding-bottom: env(safe-area-inset-bottom);
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
  }
`;

const MenuWrapper = styled.div`
  position: relative;
  background-color: var(--color-background);
  border-radius: 3px;

  &.-mobile {
    top: 0;
  }
`;

const StyledButton = styled(Button)`
  && {
    border: none;
    outline: none;
    margin-inline-start: -10px;
    &:focus-visible ${Icon} {
      outline: var(--focus-outline);
      outline-offset: -3px;
      border-radius: 4px;
    }
  }
`;

type UserMenuWidgetState = {
  menuOptions: UserMenuOption[];
  isMobileMenuOpen: boolean;
};

type UserMenuProps = {
  widgetState: MultiWidgetStateStore<UserMenuWidgetState>;
  isDesktop: boolean;
  T: TranslateFn;
  onOpen?: () => void;
};

const UserMenu = ({ widgetState, isDesktop, T, onOpen }: UserMenuProps) => {
  const [menuOptions, setMenuOptions] = useState<UserMenuOption[]>([]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const colors = useCSSColors() as Record<string, string>;
  const ref = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const isMobile = !isDesktop;

  useEffect(() => {
    const state = widgetState.getState();
    setMenuOptions(state.menuOptions || []);
    setIsMobileMenuOpen(state.isMobileMenuOpen || false);

    return widgetState.addCallback(nextState => {
      setMenuOptions(nextState.menuOptions || []);
      setIsMobileMenuOpen(nextState.isMobileMenuOpen || false);
    });
  }, [widgetState]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target;
      if (
        ref.current &&
        target instanceof Node &&
        !ref.current.contains(target) &&
        document.activeElement?.id !== 'mobileUserMenuButton'
      ) {
        widgetState.update({ isMobileMenuOpen: false });
      }
    };

    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [widgetState]);

  const handleHideMenu = () => {
    widgetState.update({ isMobileMenuOpen: false });
  };

  // The panel is portaled elsewhere in the DOM (see below), so native Tab order no
  // longer flows button -> panel -> next page element. Redirect focus manually,
  // matching the pattern already used by the desktop Submenu (see submenu.tsx).
  const handleButtonKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Tab' || event.shiftKey || !isMobileMenuOpen) {
      return;
    }
    const firstFocusable = ref.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    if (firstFocusable) {
      event.preventDefault();
      firstFocusable.focus();
    }
  };

  const handlePanelKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'Tab' || !ref.current) {
      return;
    }
    const focusables = ref.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    const isFirst = focusables[0] === document.activeElement;
    const isLast = focusables[focusables.length - 1] === document.activeElement;

    if (!event.shiftKey && isLast) {
      const moved = focusRelativeToElement(buttonRef.current, 1, ref.current);
      if (moved) {
        event.preventDefault();
        widgetState.update({ isMobileMenuOpen: false });
      }
    } else if (event.shiftKey && isFirst) {
      event.preventDefault();
      buttonRef.current?.focus();
    }
  };

  if (isMobile) {
    const panel = isMobileMenuOpen && (
      <Wrapper
        ref={ref}
        id="userMenuPanel"
        className="-mobile"
        data-testid="UserMenuMobile"
        role="dialog"
        aria-modal={false}
        aria-label={T('userMenu:User menu')}
        onKeyDown={handlePanelKeyDown}
      >
        <MenuWrapper className="-mobile">
          <MenuSelection menuOptions={menuOptions} isMobile hideMenu={handleHideMenu} />
        </MenuWrapper>
      </Wrapper>
    );
    // Portal into .atrius-map so the panel's position:absolute anchors there,
    // regardless of where this widget sits in the layout tree.
    const mapElement = panel ? getMapElement() : null;

    return (
      <>
        <StyledButton
          ref={buttonRef}
          className="userMenu"
          onClick={() => {
            if (!isMobileMenuOpen) {
              onOpen?.();
            }
            widgetState.update({ isMobileMenuOpen: !isMobileMenuOpen });
          }}
          onKeyDown={handleButtonKeyDown}
          title="User Menu"
          id="mobileUserMenuButton"
          data-testid="HeaderUserMenuButton"
          aria-haspopup="dialog"
          aria-controls="userMenuPanel"
          aria-expanded={isMobileMenuOpen}
        >
          <Icon name="submenu" cdn height={40} width={40} fillColor={colors.widgetIconFill} />
        </StyledButton>
        {panel && mapElement ? createPortal(panel, mapElement) : null}
      </>
    );
  }

  if (isDesktop) {
    return (
      <Submenu
        menuItems={menuOptions}
        tooltipContent={T('userMenu:User menu')}
        onOpen={onOpen}
        data-testid="UserMenuButtonDesktop"
      />
    );
  }

  return null;
};

export default UserMenu;
